import { actions } from "@/actions";
import { NoteType } from "@/components/note/types";
import {
  MdsDropdownIconButton,
  MdsDropdownContentList,
  MdsDropdownItemKind,
  MdsDropdownItem,
} from "@/design-system/components/dropdown";
import { MdsIconKind } from "@/design-system/components/icon";
import { MDSTooltipPlacement } from "@/mds/components/tooltip";
import { AnimationTiming } from "@/design-system/constants/common";
import { css } from "@/domains/emotion";
import { useAppStore } from "@/store";
import { INoteObservable } from "@/store/note/types";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { observer } from "mobx-react-lite";
import { withLineBreaks } from "@/domains/html/withLineBreaks";
import { renderArrayWithAnds } from "@/domains/string/renderArrayWithAnds";
import { computed } from "mobx";
import {
  ShareSheetEntityKind,
  ShareSheetModal,
  ShareSheetModalStore,
} from "@/components/modal/share-sheet";
import { EventContext } from "@/domains/metrics/context";
import { NoteObservable } from "@/store/note";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import { MDSIconButtonSize } from "@/mds";

interface NoteEditorTopBarExtraActionsDropdownButtonProps {
  note?: INoteObservable | TemplateObservable;
  noteType?: NoteType;
  inSidePanel?: boolean;
  shareSheetModal: ShareSheetModalStore;
}

export const NoteEditorTopBarExtraActionsDropdownButton =
  observer<NoteEditorTopBarExtraActionsDropdownButtonProps>(
    function NoteEditorTopBarExtraActionsDropdownButton({
      note,
      noteType = NoteType.Note,
      shareSheetModal,
    }) {
      const { store } = useAppStore();
      const pageStore = useTabPageStore("notesViewPage");
      const { tabId } = pageStore;

      if (!note) return null;
      // TODO: REmove FF before rolling out
      const canShowVersions =
        note.isAvailable &&
        !note.isSuggestionActive &&
        // Require non-empty, loaded notes to allow version history
        (note instanceof NoteObservable
          ? note.isNoteContentDocumentLoaded && !note.isNoteContentInOriginalState
          : true);
      const showingVersions = pageStore.noteVersionHistory.currentNoteId == note.id;

      const verionsHistory: MdsDropdownItem[] = canShowVersions
        ? [
            {
              id: "view-version-history",
              kind: MdsDropdownItemKind.Button,
              label: "View version history",
              iconKind: MdsIconKind.History,
              isDisabled: showingVersions,
              onClick: () => {
                pageStore.noteVersionHistory.openNoteVersionHistory(note.id);
              },
            },
          ]
        : [];

      const sharingInfo = computed(() => {
        if (!note?.canAccess) {
          return {
            isDisabled: true,
            tooltipLabel: "You lost access",
            iconKind: MdsIconKind.LockSolid,
          };
        }

        if (!note?.canWrite) {
          return {
            isDisabled: true,
            tooltipLabel: "You don't have write access",
            iconKind: MdsIconKind.Shared,
          };
        }

        if (note.hasPendingShare) {
          return {
            isDisabled: false,
            tooltipLabel: "Access changes will be applied when you’re online",
            iconKind: MdsIconKind.Spinner,
          };
        }

        if (note?.isShared) {
          const labels = note?.sharedWithLabels;
          if (labels) {
            return {
              isDisabled: false,
              tooltipLabel: withLineBreaks(
                `Shared with ${renderArrayWithAnds(
                  labels.length >= 5
                    ? [...labels.slice(0, 3), `${labels.length - 2} others`]
                    : labels
                )}`
              ),
              iconKind: MdsIconKind.Shared,
            };
          }
        }

        return {
          isDisabled: false,
          tooltipLabel: "Private to only me",
          iconKind: MdsIconKind.LockSolid,
        };
      }).get();

      const cleanUpButtons: MdsDropdownItem[] =
        note instanceof NoteObservable
          ? [
              {
                id: "clean-up",
                kind: MdsDropdownItemKind.Button,
                label: "Clean Up",
                tooltip: "Clean up notes in a single click",
                iconKind: note.isProcessingSuggestion ? MdsIconKind.Spinner : MdsIconKind.Cleanup,
                onClick: () => note.cleanup(),
              },
            ]
          : [];
      const pinButtons: MdsDropdownItem[] =
        note instanceof NoteObservable
          ? [
              {
                id: "pin",
                kind: MdsDropdownItemKind.Button,
                label: "Pin",
                tooltip: "Pin to sidebar",
                iconKind: note.isFavorited ? MdsIconKind.ThumbtackSolid : MdsIconKind.Thumbtack,
                onClick: () => note.toggleFavorite(),
              },
            ]
          : [];

      const contentList: MdsDropdownContentList = {
        items: [
          {
            id: "magic-actions",
            kind: MdsDropdownItemKind.Detail,
            text: "Magic Actions",
          },
          ...cleanUpButtons,
          {
            id: "note-actions",
            kind: MdsDropdownItemKind.Detail,
            text: "Note Actions",
          },
          ...pinButtons,
          {
            id: "share",
            kind: MdsDropdownItemKind.Button,
            label: "Share",
            isDisabled: sharingInfo.isDisabled,
            iconKind: sharingInfo.iconKind,
            tooltip: sharingInfo.tooltipLabel,
            onClick: () =>
              shareSheetModal.open({
                id: note.id,
                entityKind: ShareSheetEntityKind.Note,
                eventContext: EventContext.EditorActions,
              }),
          },
          {
            id: "copy-note-link",
            kind: MdsDropdownItemKind.Button,
            label: "Copy link",
            iconKind: MdsIconKind.Link,
            onClick: () => actions.copyNoteLinkToClipboard({ noteId: note.id, noteType }),
          },
          {
            id: "move-to-trash",
            kind: MdsDropdownItemKind.Button,
            label: "Trash",
            iconKind: MdsIconKind.Trash,
            onClick: async () => {
              if (note instanceof TemplateObservable) {
                await note.delete();
              } else {
                await note.moveToTrash();
              }
              switch (noteType) {
                case NoteType.Note:
                  store.navigation.tabs.closeTab({ tabId });
                  break;
                case NoteType.Template:
                  store.navigation.goToTemplates();
                  break;
              }
            },
          },

          ...verionsHistory,
        ],
      };

      return (
        <>
          <ShareSheetModal store={shareSheetModal} />
          <MdsDropdownIconButton
            size={MDSIconButtonSize.Medium}
            buttonClassName={moreButtonStyles}
            contentList={contentList}
            placement="below-right-alignment"
            tooltipConfig={{
              label: "More",
              placement: MDSTooltipPlacement.Top,
              delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
            }}
          />
        </>
      );
    }
  );

const moreButtonStyles = css({
  maxWidth: "36px",
});
